import { useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import type { Proyecto, Tarea, Factura, Cotizacion, ConfigTarifaCliente, CostoEstimado, GastoProyecto } from '@/lib/types'
import HitosProyecto from './HitosProyecto'
import ConsumoBolsas from './ConsumoBolsas'
import { formatCurrency, formatDate, r2 } from '@/lib/utils'
import { supabase } from '@/lib/supabase'
import { showToast } from '@/components/layout/Toast'
import {
  IconDollar, IconTimer, IconBarChart, IconCheckCircle,
  IconSend, IconEye, IconFilePlus, IconExternalLink,
  IconClock, IconFileText, IconTrendingUp, IconReceipt,
} from '@/components/icons/Icons'
import ExpenseTable from './ExpenseTable'
import ProjectBudgetAlert from './ProjectBudgetAlert'

const API_URL = import.meta.env.VITE_API_URL || 'https://api.1conecta.com'

interface Props {
  proyecto: Proyecto
  tareas: Tarea[]
  facturas: Factura[]
  cotizaciones: Cotizacion[]
  tarifaCliente: ConfigTarifaCliente | null
  onGenerarFactura: (selectedTaskIds: number[]) => void
  onRefresh: () => void
  supabaseUrl?: string
}

const ESTADO_FACTURA: Record<string, { label: string; color: string; bg: string }> = {
  pre_factura: { label: 'Borrador', color: 'var(--text-muted)', bg: 'color-mix(in srgb, var(--text-muted) 12%, transparent)' },
  borrador: { label: 'Borrador', color: 'var(--text-muted)', bg: 'color-mix(in srgb, var(--text-muted) 12%, transparent)' },
  emitida: { label: 'Emitida', color: 'var(--info)', bg: 'color-mix(in srgb, var(--info) 12%, transparent)' },
  pagada: { label: 'Pagada', color: 'var(--success)', bg: 'color-mix(in srgb, var(--success) 12%, transparent)' },
  anulada: { label: 'Anulada', color: 'var(--danger)', bg: 'color-mix(in srgb, var(--danger) 12%, transparent)' },
}

const PAYMENT_STATUS_BADGE: Record<string, { label: string; color: string; bg: string }> = {
  none: { label: 'Sin enviar', color: 'var(--text-muted)', bg: 'color-mix(in srgb, var(--text-muted) 12%, transparent)' },
  pending: { label: 'Enviado', color: 'var(--warning)', bg: 'color-mix(in srgb, var(--warning) 12%, transparent)' },
  paid: { label: 'Pagado', color: 'var(--success)', bg: 'color-mix(in srgb, var(--success) 12%, transparent)' },
  rejected: { label: 'Rechazado', color: 'var(--danger)', bg: 'color-mix(in srgb, var(--danger) 12%, transparent)' },
  cancelled: { label: 'Cancelado', color: 'var(--text-muted)', bg: 'color-mix(in srgb, var(--text-muted) 12%, transparent)' },
}

type SubTab = 'aprobadas' | 'cobros' | 'costos' | 'cotizaciones'

interface Conteos { facturables: number; costos: number; cotizaciones: number }

// Solo tablas que crecen con el tiempo. Lo que es de lectura rapida (KPIs,
// presupuesto, horas, bolsas, hitos) se queda fuera y siempre a la vista.
const SUBTABS: { clave: SubTab; label: string; visible: (c: Conteos) => boolean; contador: (c: Conteos) => number }[] = [
  { clave: 'aprobadas', label: 'Tareas aprobadas', visible: () => true, contador: c => c.facturables },
  { clave: 'cobros', label: 'Cobros', visible: () => true, contador: () => 0 },
  { clave: 'costos', label: 'Costos', visible: c => c.costos > 0, contador: c => c.costos },
  { clave: 'cotizaciones', label: 'Cotizaciones', visible: c => c.cotizaciones > 0, contador: c => c.cotizaciones },
]

export default function CajaProyecto({
  proyecto, tareas, facturas, cotizaciones, tarifaCliente, onGenerarFactura, onRefresh,
}: Props) {
  const moneda = proyecto.moneda || 'USD'
  const tarifa = tarifaCliente?.tarifa_hora ?? 0

  // ── P&L Data ──
  const [costosEstimados, setCostosEstimados] = useState<CostoEstimado[]>([])
  const [gastosProyecto, setGastosProyecto] = useState<GastoProyecto[]>([])
  const [loadingPL, setLoadingPL] = useState(true)

  // Local state for workbench
  const [adjustments, setAdjustments] = useState<Record<number, number>>({})
  const [globalDiscount, setGlobalDiscount] = useState(0)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [sub, setSub] = useState<SubTab>('aprobadas')
  const [sendingPayment, setSendingPayment] = useState<number | null>(null)
  const [hoursEdits, setHoursEdits] = useState<Record<number, string>>({})
  const [savingHoursId, setSavingHoursId] = useState<number | null>(null)

  // Load P&L data (costos_estimados + gastos_proyecto)
  async function loadPLData() {
    setLoadingPL(true)
    try {
      // Load costos_estimados for all cotizaciones linked to this project
      const cotIds = cotizaciones.map(c => c.id)
      let allCostos: CostoEstimado[] = []
      if (cotIds.length > 0) {
        const res = await supabase.from('costos_estimados').select('*')
        if (res.data) {
          allCostos = (res.data as CostoEstimado[]).filter(c => cotIds.includes(c.cotizacion_id))
        }
      }
      const gastosRes = await supabase.from('gastos_proyecto').select('*').eq('proyecto_id', proyecto.id)
      setCostosEstimados(allCostos)
      setGastosProyecto((gastosRes.data ?? []) as GastoProyecto[])
    } catch {
      // silent — non-critical data
    } finally {
      setLoadingPL(false)
    }
  }

  useEffect(() => {
    loadPLData()
  }, [proyecto.id, cotizaciones.length]) // eslint-disable-line react-hooks/exhaustive-deps

  // Send MP payment link
  async function handleSendPayment(facturaId: number) {
    setSendingPayment(facturaId)
    try {
      const { data: session } = await supabase.auth.getSession()
      const token = session?.session?.access_token
      if (!token) throw new Error('No autenticado')

      const res = await fetch(
        `${API_URL}/api/facturas/${facturaId}/mp-send-payment-email`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        }
      )
      const result = await res.json()
      if (!res.ok) throw new Error(result.error || 'Error enviando cobro')

      showToast(`Link de pago enviado a ${result.sent_to?.join(', ')}`, 'success')
      onRefresh()
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Error enviando link de pago'
      showToast(message, 'error')
    } finally {
      setSendingPayment(null)
    }
  }

  // Las cotizaciones que llegan son las VINCULADAS a este proyecto. La primera
  // se usa para la ficha de abajo; el importe cotizado suma todas, porque un
  // proyecto puede tener varias (el MILESTONE 6 tiene tres).
  const cotizacion = useMemo(() => cotizaciones[0] ?? null, [cotizaciones])
  const totalCotizado = useMemo(
    () => r2(cotizaciones.reduce((s, c) => s + (c.total || 0), 0)),
    [cotizaciones],
  )

  // Filter facturas for this project. DAT-03: usa la FK real proyecto_id cuando
  // existe; cae al match por nombre solo para facturas sin proyecto_id (legado).
  const projectFacturas = useMemo(() => {
    return facturas.filter(f =>
      f.proyecto_id === proyecto.id ||
      (f.proyecto_id == null && !!f.proyecto_nombre && !!proyecto.nombre &&
        f.proyecto_nombre.toLowerCase() === proyecto.nombre.toLowerCase())
    )
  }, [facturas, proyecto.id, proyecto.nombre])

  // Financial calculations (enhanced with P&L)
  const finance = useMemo(() => {
    const presupuesto = proyecto.presupuesto || 0
    const cotizado = totalCotizado
    const facturasActivas = projectFacturas.filter(f => f.estado !== 'anulada')
    const facturado = r2(facturasActivas.reduce((s, f) => s + (f.total || 0), 0))
    const porCobrar = r2(facturasActivas.filter(f => f.estado === 'emitida').reduce((s, f) => s + (f.total || 0), 0))
    const cobrado = r2(facturasActivas.filter(f => f.estado === 'pagada').reduce((s, f) => s + (f.total || 0), 0))
    const presupuestoPct = presupuesto > 0 ? Math.round((facturado / presupuesto) * 100) : 0

    // P&L additions
    const totalCostosEstimados = r2(costosEstimados.reduce((s, c) => s + c.monto, 0))
    const totalGastosReales = r2(gastosProyecto.reduce((s, g) => s + g.monto, 0))
    const margenReal = r2(cobrado - totalGastosReales)

    return { presupuesto, cotizado, facturado, porCobrar, cobrado, presupuestoPct, totalCostosEstimados, totalGastosReales, margenReal }
  }, [proyecto.presupuesto, totalCotizado, projectFacturas, costosEstimados, gastosProyecto])

  // Hours calculations
  const hours = useMemo(() => {
    const estimadas = r2(tareas.reduce((s, t) => s + (t.horas_estimadas || 0), 0))
    const reales = r2(tareas.reduce((s, t) => s + (t.horas_reales || 0), 0))
    const eficiencia = reales > 0 ? Math.round((estimadas / reales) * 100) : 0
    return { estimadas, reales, eficiencia }
  }, [tareas])

  // Billable tasks (completed + approved, not yet invoiced)
  const facturables = useMemo(() =>
    tareas.filter(t =>
      t.estado === 'completada' &&
      t.estado_aprobacion === 'aprobada' &&
      !t.factura_id
    ),
    [tareas]
  )

  // Initialize selected IDs when facturables change
  useMemo(() => {
    setSelectedIds(new Set(facturables.map(t => t.id)))
  }, [facturables.length]) // eslint-disable-line react-hooks/exhaustive-deps

  // Calculate task subtotals
  function getTaskSubtotal(t: Tarea): number {
    if (t.tipo_cobro === 'por_horas' && t.horas_estimadas > 0 && tarifa > 0) {
      return r2(t.horas_estimadas * tarifa)
    }
    return t.monto || 0
  }

  function getTaskTotal(t: Tarea): number {
    return r2(getTaskSubtotal(t) + (adjustments[t.id] || 0))
  }

  // Totals for selected tasks
  const selectedTasks = facturables.filter(t => selectedIds.has(t.id))
  const subtotal = r2(selectedTasks.reduce((s, t) => s + getTaskTotal(t), 0))
  const discountAmount = r2(subtotal * globalDiscount / 100)
  const grandTotal = r2(subtotal - discountAmount)
  const totalHoras = r2(selectedTasks.reduce((s, t) => s + (t.horas_estimadas || 0), 0))

  function toggleTask(id: number) {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function toggleAll() {
    if (selectedIds.size === facturables.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(facturables.map(t => t.id)))
    }
  }

  function setAdjustment(tareaId: number, value: number) {
    setAdjustments(prev => ({ ...prev, [tareaId]: value }))
  }

  async function handleToggleExonerar(t: { id: number; tipo_cobro: string }) {
    const newTipo = t.tipo_cobro === 'sin_costo' ? 'por_horas' : 'sin_costo'
    try {
      const payload: Record<string, unknown> = { tipo_cobro: newTipo }
      if (newTipo === 'sin_costo') payload.monto = 0
      await supabase.from('tareas').update(payload as never).eq('id', t.id)
      onRefresh()
    } catch {
      showToast('Error al cambiar cobro', 'error')
    }
  }

  async function saveHours(tareaId: number, newHoras: number, tarea: Tarea) {
    if (newHoras === tarea.horas_estimadas) return
    setSavingHoursId(tareaId)
    try {
      const updates: Record<string, number> = { horas_estimadas: newHoras }
      if (tarea.tipo_cobro === 'por_horas' && tarifa > 0) {
        updates.monto = r2(newHoras * tarifa)
      }
      await supabase.from('tareas').update(updates as never).eq('id', tareaId)
      onRefresh()
    } catch {
      showToast('Error al guardar horas', 'error')
    } finally {
      setSavingHoursId(null)
    }
  }

  // Budget bar color
  const barColor = finance.presupuestoPct > 100 ? 'var(--danger)' :
    finance.presupuestoPct >= 80 ? '#f39c12' : 'var(--success)'

  return (
    <div className="caja-proyecto">
      {/* ── Enhanced KPI Row (6 cards) ── */}
      <div className="caja-grid caja-grid-6">
        <div className="caja-card">
          <div className="caja-card-icon" style={{ background: 'color-mix(in srgb, var(--primary-light) 12%, transparent)', color: 'var(--primary)' }}>
            <IconBarChart size={20} />
          </div>
          <div className="caja-card-body">
            <span className="caja-card-label">Presupuesto</span>
            <span className="caja-card-value">
              {finance.presupuesto > 0 ? formatCurrency(finance.presupuesto, moneda) : '—'}
            </span>
          </div>
        </div>

        <div className="caja-card">
          <div className="caja-card-icon" style={{ background: 'color-mix(in srgb, var(--primary-light) 12%, transparent)', color: 'var(--primary-light)' }}>
            <IconFileText size={20} />
          </div>
          <div className="caja-card-body">
            <span className="caja-card-label">Costos Estimados</span>
            <span className="caja-card-value">
              {finance.totalCostosEstimados > 0 ? formatCurrency(finance.totalCostosEstimados, moneda) : '—'}
            </span>
          </div>
        </div>

        <div className="caja-card">
          <div className="caja-card-icon" style={{ background: 'color-mix(in srgb, var(--warning) 12%, transparent)', color: 'var(--warning)' }}>
            <IconReceipt size={20} />
          </div>
          <div className="caja-card-body">
            <span className="caja-card-label">Gastos Reales</span>
            <span className="caja-card-value" style={{ color: finance.totalGastosReales > finance.totalCostosEstimados && finance.totalCostosEstimados > 0 ? 'var(--danger)' : undefined }}>
              {finance.totalGastosReales > 0 ? formatCurrency(finance.totalGastosReales, moneda) : '—'}
            </span>
          </div>
        </div>

        <div className="caja-card">
          <div className="caja-card-icon" style={{ background: 'color-mix(in srgb, var(--info) 12%, transparent)', color: 'var(--info)' }}>
            <IconDollar size={20} />
          </div>
          <div className="caja-card-body">
            <span className="caja-card-label">Facturado</span>
            <span className="caja-card-value" style={{ color: 'var(--accent-primary)' }}>
              {formatCurrency(finance.facturado, moneda)}
            </span>
          </div>
        </div>

        <div className="caja-card">
          <div className="caja-card-icon" style={{ background: 'color-mix(in srgb, var(--success) 12%, transparent)', color: 'var(--success)' }}>
            <IconCheckCircle size={20} />
          </div>
          <div className="caja-card-body">
            <span className="caja-card-label">Cobrado</span>
            <span className="caja-card-value" style={{ color: 'var(--success)' }}>
              {formatCurrency(finance.cobrado, moneda)}
            </span>
          </div>
        </div>

        <div className="caja-card">
          <div className="caja-card-icon" style={{ background: finance.margenReal >= 0 ? 'color-mix(in srgb, var(--success) 12%, transparent)' : 'color-mix(in srgb, var(--danger) 12%, transparent)', color: finance.margenReal >= 0 ? 'var(--success)' : 'var(--danger)' }}>
            <IconTrendingUp size={20} />
          </div>
          <div className="caja-card-body">
            <span className="caja-card-label">Margen Real</span>
            <span className="caja-card-value" style={{ color: finance.margenReal >= 0 ? 'var(--success)' : 'var(--danger)' }}>
              {finance.cobrado > 0 || finance.totalGastosReales > 0
                ? formatCurrency(finance.margenReal, moneda)
                : '—'}
            </span>
          </div>
        </div>
      </div>

      {/* Budget progress bar */}
      {finance.presupuesto > 0 && (
        <div className="caja-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
              Presupuesto consumido
            </span>
            <span style={{ fontSize: '0.85rem', fontWeight: 700, color: barColor }}>
              {finance.presupuestoPct}%
            </span>
          </div>
          <div className="caja-progress">
            <div
              className="caja-progress-fill"
              style={{
                width: `${Math.min(finance.presupuestoPct, 100)}%`,
                background: barColor,
              }}
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: '0.78rem', color: 'var(--text-muted)' }}>
            <span>Facturado: {formatCurrency(finance.facturado, moneda)}</span>
            <span>Presupuesto: {formatCurrency(finance.presupuesto, moneda)}</span>
          </div>
        </div>
      )}

      {/* Hours summary */}
      <div className="caja-grid caja-grid-3">
        <div className="caja-card caja-card-compact">
          <div className="caja-card-icon-sm" style={{ color: 'var(--accent-primary)' }}>
            <IconTimer size={16} />
          </div>
          <div>
            <span className="caja-card-label">Horas estimadas</span>
            <span className="caja-card-value-sm">{hours.estimadas}h</span>
          </div>
        </div>

        <div className="caja-card caja-card-compact">
          <div className="caja-card-icon-sm" style={{ color: hours.reales > hours.estimadas ? 'var(--danger)' : 'var(--success)' }}>
            <IconClock size={16} />
          </div>
          <div>
            <span className="caja-card-label">Horas reales</span>
            <span className="caja-card-value-sm">{hours.reales}h</span>
          </div>
        </div>

        <div className="caja-card caja-card-compact">
          <div className="caja-card-icon-sm" style={{ color: hours.eficiencia >= 100 ? 'var(--success)' : hours.eficiencia > 0 ? 'var(--warning)' : 'var(--text-muted)' }}>
            <IconBarChart size={16} />
          </div>
          <div>
            <span className="caja-card-label">Eficiencia</span>
            <span className="caja-card-value-sm">
              {hours.reales > 0 ? `${hours.eficiencia}%` : '—'}
            </span>
          </div>
        </div>
      </div>

      {/* Bolsas de horas: solo en contratos de soporte. */}
      <ConsumoBolsas proyecto={proyecto} tareas={tareas} />

      {/* ── Cronograma de pagos ──
           Se lee del endpoint de hitos, que es el que aplica el IGV y empareja
           cada cuota con su factura. Calcularlo aqui mostraba el importe sin
           IGV y emparejaba por monto, que cruza dos cuotas del mismo valor. */}
      <HitosProyecto proyectoId={proyecto.id} moneda={moneda} modeloCobro={proyecto.modelo_cobro} />

      {/* Sub-pestanas. Solo para las tablas que crecen mes a mes: dejarlas
           todas apiladas obligaba a scrollear media pagina para llegar a los
           cobros. Lo de arriba es la vista principal y no se esconde. */}
      <div className="caja-subtabs">
        {SUBTABS.filter(t => t.visible({ facturables: facturables.length, costos: costosEstimados.length + gastosProyecto.length, cotizaciones: cotizaciones.length })).map(t => (
          <button
            key={t.clave}
            className={`tag-chip ${sub === t.clave ? 'active' : ''}`}
            onClick={() => setSub(t.clave)}
          >
            {t.label}
            {t.contador({ facturables: facturables.length, costos: costosEstimados.length + gastosProyecto.length, cotizaciones: cotizaciones.length }) > 0 && (
              <span className="caja-subtabs__contador">
                {t.contador({ facturables: facturables.length, costos: costosEstimados.length + gastosProyecto.length, cotizaciones: cotizaciones.length })}
              </span>
            )}
          </button>
        ))}
      </div>

      {sub === 'aprobadas' && (<>
      {/* ── Workbench: Tareas Aprobadas (listas para facturar) ── */}
      <div className="caja-section">
        <div className="caja-section-header">
          <h3 style={{ margin: 0, fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: 6 }}>
            <IconCheckCircle size={16} /> Tareas Aprobadas
            {facturables.length > 0 && (
              <span style={{ fontSize: '0.78rem', fontWeight: 400, color: 'var(--text-muted)' }}>
                ({facturables.length} {facturables.length === 1 ? 'tarea' : 'tareas'})
              </span>
            )}
          </h3>
          {selectedTasks.length > 0 && (
            <button className="btn btn-primary btn-sm" onClick={() => onGenerarFactura(selectedTasks.map(t => t.id))} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <IconFilePlus size={14} /> Generar Cobro ({selectedTasks.length})
            </button>
          )}
        </div>

        {facturables.length === 0 ? (
          <div style={{ padding: '24px 16px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
            No hay tareas aprobadas pendientes de facturar.
            <br />
            <span style={{ fontSize: '0.8rem' }}>Aprueba tareas completadas desde el Kanban.</span>
          </div>
        ) : (
          <>
            <div className="table-container" style={{ marginTop: 0 }}>
              <table className="caja-workbench-table">
                <thead>
                  <tr>
                    <th style={{ width: 32 }}>
                      <input
                        type="checkbox"
                        checked={selectedIds.size === facturables.length}
                        onChange={toggleAll}
                        title="Seleccionar todas"
                      />
                    </th>
                    <th>Tarea</th>
                    <th style={{ textAlign: 'center', width: 70 }}>Cobro</th>
                    <th style={{ textAlign: 'right', width: 70 }}>Horas</th>
                    <th style={{ textAlign: 'right', width: 110 }}>Costo/h</th>
                    <th style={{ textAlign: 'right', width: 120 }}>Subtotal</th>
                    <th style={{ textAlign: 'right', width: 90 }}>Ajuste</th>
                    <th style={{ textAlign: 'right', width: 120 }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {facturables.map(t => {
                    const isExonerada = t.tipo_cobro === 'sin_costo'
                    const sub = getTaskSubtotal(t)
                    const adj = adjustments[t.id] || 0
                    const total = getTaskTotal(t)
                    const isSelected = selectedIds.has(t.id)
                    const tipoBadge = t.tipo === 'bug'
                      ? { label: 'Bug', color: 'var(--danger)', bg: 'color-mix(in srgb, var(--danger) 12%, transparent)' }
                      : t.tipo === 'mejora'
                        ? { label: 'Mejora', color: 'var(--success)', bg: 'color-mix(in srgb, var(--success) 12%, transparent)' }
                        : null

                    return (
                      <tr key={t.id} style={{ opacity: isSelected ? 1 : 0.45 }}>
                        <td>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleTask(t.id)}
                          />
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            {tipoBadge && (
                              <span style={{
                                fontSize: '0.68rem', fontWeight: 700, padding: '1px 6px',
                                borderRadius: 4, background: tipoBadge.bg, color: tipoBadge.color,
                                flexShrink: 0, letterSpacing: 0.3,
                              }}>
                                {tipoBadge.label}
                              </span>
                            )}
                            <span style={{ fontWeight: 500, fontSize: '0.85rem' }}>{t.titulo}</span>
                          </div>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <button
                            type="button"
                            onClick={() => handleToggleExonerar(t)}
                            title={isExonerada ? 'Click para cobrar' : 'Click para exonerar'}
                            style={{
                              cursor: 'pointer', border: 'none', borderRadius: 4, padding: '2px 8px',
                              fontSize: '0.72rem', fontWeight: 600, transition: 'all 0.15s ease',
                              background: isExonerada ? 'color-mix(in srgb, var(--danger) 12%, transparent)' : 'rgba(46,204,113,0.12)',
                              color: isExonerada ? 'var(--danger)' : 'var(--success)',
                            }}
                          >
                            {isExonerada ? 'Exonerado' : 'Cobrable'}
                          </button>
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <input
                            type="number"
                            min={0}
                            step={0.5}
                            className="quick-edit-input"
                            value={hoursEdits[t.id] ?? t.horas_estimadas}
                            onChange={e => setHoursEdits(prev => ({ ...prev, [t.id]: e.target.value }))}
                            onBlur={() => {
                              const val = Math.max(0, Number(hoursEdits[t.id] ?? t.horas_estimadas))
                              setHoursEdits(prev => { const n = { ...prev }; delete n[t.id]; return n })
                              saveHours(t.id, val, t)
                            }}
                            onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }}
                            disabled={savingHoursId === t.id || isExonerada}
                            style={{ width: 60, textAlign: 'right' }}
                            title="Editar horas estimadas"
                          />
                        </td>
                        <td style={{ textAlign: 'right', fontSize: '0.85rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                          {t.tipo_cobro === 'por_horas' && tarifa > 0
                            ? formatCurrency(tarifa, moneda)
                            : '—'
                          }
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 600, fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                          {sub > 0 ? formatCurrency(sub, moneda) : '—'}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <input
                            type="number"
                            className={`quick-edit-input${adj !== 0 ? (adj > 0 ? ' quick-edit-positive' : ' quick-edit-danger') : ''}`}
                            value={adj || ''}
                            placeholder="0"
                            onChange={e => setAdjustment(t.id, Number(e.target.value))}
                            style={{ width: 75 }}
                            title="Ajuste: positivo = adicional, negativo = descuento"
                            disabled={isExonerada}
                          />
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 700, fontSize: '0.88rem', whiteSpace: 'nowrap', color: isExonerada ? 'var(--text-muted)' : total > 0 ? 'var(--accent-primary)' : 'var(--text-muted)' }}>
                          {isExonerada ? (
                            <span style={{ textDecoration: 'line-through', opacity: 0.5 }}>
                              {t.horas_estimadas > 0 && tarifa > 0 ? formatCurrency(r2(t.horas_estimadas * tarifa), moneda) : '—'}
                            </span>
                          ) : (
                            total > 0 ? formatCurrency(total, moneda) : '—'
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <div className="caja-workbench-totals">
              <div className="caja-workbench-totals-row">
                <span>Horas totales</span>
                <span style={{ fontWeight: 600 }}>{totalHoras}h</span>
              </div>
              <div className="caja-workbench-totals-row">
                <span>Subtotal ({selectedTasks.length} {selectedTasks.length === 1 ? 'tarea' : 'tareas'})</span>
                <span style={{ fontWeight: 600 }}>{formatCurrency(subtotal, moneda)}</span>
              </div>
              <div className="caja-workbench-totals-row">
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  Descuento global
                  <input
                    type="number"
                    className="quick-edit-input"
                    value={globalDiscount || ''}
                    placeholder="0"
                    min={0}
                    max={100}
                    onChange={e => setGlobalDiscount(Math.max(0, Math.min(100, Number(e.target.value))))}
                    style={{ width: 55 }}
                  />
                  <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>%</span>
                </span>
                <span style={{ fontWeight: 600, color: discountAmount > 0 ? 'var(--danger)' : 'var(--text-muted)' }}>
                  {discountAmount > 0 ? `- ${formatCurrency(discountAmount, moneda)}` : '—'}
                </span>
              </div>
              <div className="caja-workbench-totals-row caja-workbench-grand-total">
                <span>Total a facturar</span>
                <span>{formatCurrency(grandTotal, moneda)}</span>
              </div>
            </div>
          </>
        )}
      </div>

      </>)}

      {sub === 'cobros' && (<>
      {/* Invoices table */}
      <div className="caja-section">
        <div className="caja-section-header">
          <h3 style={{ margin: 0, fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: 6 }}>
            <IconDollar size={16} /> Cobros del Proyecto
          </h3>
          {finance.porCobrar > 0 && (
            <span style={{ fontSize: '0.8rem', color: 'var(--info)', fontWeight: 600 }}>
              Por cobrar: {formatCurrency(finance.porCobrar, moneda)}
            </span>
          )}
        </div>

        {projectFacturas.length === 0 ? (
          <div style={{ padding: '24px 16px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
            No hay cobros generados para este proyecto
          </div>
        ) : (
          <div className="table-container" style={{ marginTop: 0 }}>
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Fecha</th>
                  <th>Monto</th>
                  <th>Estado</th>
                  <th>Pago</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {projectFacturas.map(f => {
                  const est = ESTADO_FACTURA[f.estado] || ESTADO_FACTURA.borrador
                  return (
                    <tr key={f.id}>
                      <td style={{ fontFamily: 'monospace', fontWeight: 600 }}>{f.numero}</td>
                      <td>{f.fecha_emision ? formatDate(f.fecha_emision) : '—'}</td>
                      <td style={{ fontWeight: 600, color: 'var(--accent-primary)' }}>
                        {formatCurrency(f.total, f.moneda)}
                      </td>
                      <td>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          padding: '2px 10px', borderRadius: 12, fontSize: '0.78rem',
                          fontWeight: 600, background: est.bg, color: est.color,
                        }}>
                          {est.label}
                        </span>
                      </td>
                      <td>
                        {(() => {
                          const flowSt = PAYMENT_STATUS_BADGE[f.flow_status || 'none'] || PAYMENT_STATUS_BADGE.none
                          return (
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', gap: 4,
                              padding: '2px 10px', borderRadius: 12, fontSize: '0.78rem',
                              fontWeight: 600, background: flowSt.bg, color: flowSt.color,
                            }}>
                              {flowSt.label}
                            </span>
                          )
                        })()}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <Link
                            to={`/facturas/${f.id}`}
                            className="btn btn-sm btn-secondary"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.78rem' }}
                          >
                            <IconEye size={13} /> Ver
                          </Link>
                          {f.estado === 'emitida' && f.flow_status !== 'paid' && (
                            <button
                              className="btn btn-sm"
                              style={{
                                display: 'inline-flex', alignItems: 'center', gap: 4,
                                fontSize: '0.78rem',
                                background: f.flow_status === 'pending' ? 'color-mix(in srgb, var(--warning) 12%, transparent)' : 'rgba(5,150,105,0.12)',
                                color: f.flow_status === 'pending' ? 'var(--warning)' : 'var(--success)',
                                border: 'none',
                              }}
                              onClick={() => handleSendPayment(f.id)}
                              disabled={sendingPayment === f.id}
                              title={f.flow_status === 'pending' ? 'Reenviar link de pago' : 'Enviar link de pago via Mercado Pago'}
                            >
                              <IconSend size={12} />
                              {sendingPayment === f.id ? 'Enviando...' : f.flow_status === 'pending' ? 'Reenviar' : 'Enviar Link de Pago'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      </>)}

      {sub === 'costos' && (<>
      {/* ── Costos del Proyecto (P&L section) ── */}
      {!loadingPL && (costosEstimados.length > 0 || gastosProyecto.length > 0) && (
        <div className="caja-section">
          <div className="caja-section-header">
            <h3 style={{ margin: 0, fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: 6 }}>
              <IconReceipt size={16} /> Costos del Proyecto
            </h3>
          </div>

          <ProjectBudgetAlert
            presupuesto={finance.totalCostosEstimados}
            gastado={finance.totalGastosReales}
            moneda={moneda}
          />

          <ExpenseTable
            costosEstimados={costosEstimados}
            gastosProyecto={gastosProyecto}
            moneda={moneda}
            proyectoId={proyecto.id}
            onGastoAdded={() => { loadPLData(); onRefresh() }}
          />
        </div>
      )}

      </>)}

      {sub === 'cotizaciones' && (<>
      {/* Cotización reference */}
      {cotizacion && (
        <div className="caja-section" style={{ background: 'var(--bg-tertiary)' }}>
          <div className="caja-section-header">
            <h3 style={{ margin: 0, fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: 6 }}>
              <IconFileText size={16} /> Cotizacion Vinculada
            </h3>
            <Link
              to={`/cotizaciones/${cotizacion.id}`}
              className="btn btn-sm btn-secondary"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.78rem' }}
            >
              <IconExternalLink size={13} /> Ver Cotizacion
            </Link>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, fontSize: '0.85rem' }}>
            <div>
              <span style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>Numero</span>
              <div style={{ fontWeight: 600 }}>#{cotizacion.numero}</div>
            </div>
            <div>
              <span style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>Fecha</span>
              <div style={{ fontWeight: 500 }}>{cotizacion.fecha_emision ? formatDate(cotizacion.fecha_emision) : '—'}</div>
            </div>
            <div>
              <span style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>Total</span>
              <div style={{ fontWeight: 700, color: 'var(--accent-primary)' }}>{formatCurrency(cotizacion.total, cotizacion.moneda)}</div>
            </div>
          </div>
        </div>
      )}
      </>)}

    </div>
  )
}
